import Decimal from 'decimal.js';
import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/system';

import { CreatePaymentPlanDto } from 'src/api/paymentPlanService/dto/CreatePaymentPlanDto';
import { Spinner } from 'src/components/spinner';
import { useCreatePaymentPlan } from 'src/lib/hooks/use-create-payment-plan';
import { useCalculateBookingDetails } from 'src/lib/hooks/use-post-calculate-booking-details';
import { useCreateBasket } from 'src/lib/hooks/use-post-create-basket';
import { Basket, clearBasket, setBasket, setBasketError } from 'src/store/slices/basket-slice';
import { routes } from 'src/constants/routes';
import { ErrorCode } from 'src/shared/enum/ErrorCode';
import {
    clearPaymentPlan,
    selectUserChoices,
    setCreatedPaymentPlan,
    setUserChoices,
} from 'src/store/slices/payment-plan-slice';
import { setSnackbarState } from 'src/store/slices/snackbar-slice';
import { ValidationErrors } from 'src/shared/enum/ValidationErrors';
import store from 'src/store/store';
import {
    setBasketStorage,
    setBookingStorage,
    setPaymentPlanSelectionStorage,
    updateRedirectState,
} from 'src/utils/local-storage-versioning';
import { InstalmentType } from 'src/shared/enum/InstalmentType';
import {
    selectBookingDetails,
    setBookingDetails,
    setBookingError,
    setBuildingData,
    setRoomData,
} from 'src/store/slices/booking-slice';
import { Term } from 'src/store/types/booking-types';
import { useHandleUserAuth } from 'src/utils/handle-user-auth';
import { isUserAuthenticated } from 'src/auth/auth0';

import { styles } from './styles';

export const BrochureCallbackPage: FC = () => {
    const [searchParams] = useSearchParams();

    const { mutateAsync: createPaymentPlan, data: paymentPlanData } = useCreatePaymentPlan();
    const { mutateAsync: fetchBookingDetails, data: bookingDetailsData } =
        useCalculateBookingDetails();
    const { mutateAsync: createBasketAsync, data: createBasketData } = useCreateBasket();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const LEAD_SOURCE_DIRECT = 'direct';
    const LEAD_MEDIUM = '-';
    const LEAD_CAMPAIGN = '-';
    const VENDOR_STUDENT_BOOKING = 'studentbookingvendor';

    const academicYear = searchParams.get('academicYear') || '';
    const fillerWeekEligible = searchParams.get('fillerWeekEligible');
    const instalmentId = searchParams.get('instalmentId');
    const isRebooker = searchParams.get('isRebooker');
    const promotionId = searchParams.get('promotionId');
    const roomCode = searchParams.get('roomCode') || '';
    const selectedStudents = searchParams.get('students');
    const termsDetails = useSelector(selectBookingDetails);
    const userChoices = useSelector(selectUserChoices);
    const { handleUserAuth } = useHandleUserAuth();

    const fetchCalculateBookingDetails = () => {
        fetchBookingDetails(
            {
                roomName: roomCode,
                academicYear,
            },
            {
                onSuccess: (data) => {
                    dispatch(setRoomData(data.room));
                    dispatch(setBookingDetails(data.bookingDetails));
                    dispatch(setBuildingData(data.building));
                    setBookingStorage({
                        id: null,
                        room: data.room,
                        details: data.bookingDetails,
                        building: data.building,
                        error: null,
                        bookingRecordId: null,
                    });
                },
                onError: () => {
                    dispatch(setBookingError(t('booking_summary_error_fetching_data')));
                },
            }
        );
    };

    const postCreatePaymentPlan = async () => {
        if (!bookingDetailsData) return null;

        const userSession = await isUserAuthenticated();

        if (userSession) {
            await handleUserAuth(userSession);
        }

        const { room } = bookingDetailsData;
        let instalmentType: string | null = null;
        let selectedTermName: string | null = null;

        Object.values(room.rental_contract_ids).forEach((rentalContractId) => {
            const { name, options } = rentalContractId;

            const foundOption = Object.values(options).find((option) => option.id === instalmentId);

            if (foundOption) {
                instalmentType = foundOption.name;
                selectedTermName = name;
            }
        });

        if (!instalmentType || !selectedTermName) {
            navigate(routes.failure.index);
            return null;
        }

        const selectedTermDetails = termsDetails?.terms.find(
            (term) => term.name === selectedTermName
        );

        const payload = new CreatePaymentPlanDto({
            academicYear,
            basePrice: room.base_price,
            fillerWeekEligible: !!fillerWeekEligible,
            instalmentId,
            instalmentType,
            isRebooker: !!isRebooker,
            promotionId,
            roomCode,
            selectedStudents: selectedStudents ? Number(selectedStudents) : 1,
            selectedTerm: selectedTermName,
        });

        return createPaymentPlan(payload, {
            onSuccess(data) {
                dispatch(setCreatedPaymentPlan(data));
                dispatch(
                    setUserChoices({
                        numberOfStudents: Number(selectedStudents),
                        term: selectedTermDetails as Term,
                        instalmentType: instalmentType as InstalmentType,
                        instalmentId,
                    })
                );
                setPaymentPlanSelectionStorage({
                    createdPaymentPlan: data,
                    userChoices: {
                        numberOfStudents: Number(selectedStudents),
                        term: selectedTermDetails as Term,
                        instalmentType: instalmentType as InstalmentType,
                        instalmentId,
                    },
                });
            },
        });
    };

    const createBasket = async () => {
        if (!bookingDetailsData || !paymentPlanData || !userChoices) return;
        if (!userChoices.term) {
            navigate(routes.failure.index);
            return;
        }

        const { room } = bookingDetailsData;

        const createBasketDto = {
            id: null,
            user: null,
            products: [
                {
                    id: bookingDetailsData.room.id,
                    room_code: bookingDetailsData.room.room_code,
                    academic_year: bookingDetailsData.room.academic_year,
                    building_id: bookingDetailsData.room.building_id,
                    development_id: bookingDetailsData.building.development_id,
                    price: room.monthly_price ? room.monthly_price : room.base_price,
                    currency: bookingDetailsData.room.currency,
                    deposit: new Decimal(0),
                    advance_payment_weeks: bookingDetailsData.room.advance_payment_weeks,
                    deposit_prepaid: bookingDetailsData.room.deposit_prepaid,
                    selected_students: selectedStudents ? parseInt(selectedStudents, 10) : 1,
                    term: userChoices.term.name,
                    beds: bookingDetailsData.room.number_of_beds,
                    payment_plan_id: instalmentId,
                    instalment_type: userChoices.instalmentType,
                    cbl: false,
                    stl: false,
                    occupants: [],
                    selected_price: {
                        fee: paymentPlanData.weeklyPrice,
                        today: paymentPlanData.paymentOptions[0].price,
                        non_discounted: paymentPlanData.subtotal,
                    },
                    legal_representative: null,
                    promotion_id: paymentPlanData.promotion?.id ?? null,
                },
            ],
            options: { rebooker: false, rebookerPhase: null, fillerWeekEligible: false },
            tracking: {
                lead_source: LEAD_SOURCE_DIRECT,
                lead_medium: LEAD_MEDIUM,
                lead_campaign: LEAD_CAMPAIGN,
            },
            vendor: VENDOR_STUDENT_BOOKING,
        };

        try {
            await createBasketAsync({
                ...createBasketDto,
                error: null,
                room_mates: [],
                licence_agreement_signed_at: null,
                booking_terms_signed_at: null,
                created_date: null,
                updated_date: null,
            } as Basket);
        } catch (error) {
            if (error.response.data.data.room[0] === ValidationErrors.ROOM_NOT_AVAILABLE) {
                navigate(routes.roomNotAvailable.index);
                return;
            }

            navigate(routes.failure.index);
        }
    };

    const proceedToBookingDetails = async () => {
        try {
            if (!createBasketData || !paymentPlanData) return;
            dispatch(setBasket(createBasketData));
            setBasketStorage(createBasketData);
            updateRedirectState(roomCode, academicYear, promotionId, null, null, null);

            navigate(routes.bookingDetails.index);
        } catch (err) {
            if (err.response.code === ErrorCode.ERROR_BASKET_OUTDATED) {
                dispatch(clearBasket());
                dispatch(setSnackbarState({ message: t('basket_out_of_date_message') }));
                return;
            }
            if (err.response.data.code === ErrorCode.PROMOTION_EXPIRED) {
                dispatch(clearBasket());
                dispatch(clearPaymentPlan());
                dispatch(setSnackbarState({ message: t('promotion_expired_message') }));
                fetchCalculateBookingDetails();
                return;
            }
            if (err.response.data && err.response.data.data && err.response.data.data.room) {
                if (err.response.data.data.room[0] === ValidationErrors.ROOM_NOT_AVAILABLE) {
                    navigate(routes.roomNotAvailable.index);
                    return;
                }
                if (err.response.data.data.room[0] === ValidationErrors.BASE_PRICE_MISMATCH) {
                    fetchCalculateBookingDetails();
                    dispatch(setSnackbarState({ message: t('room_price_updated_message') }));
                    return;
                }
            }
            store.dispatch(setBasketError(err.error));
        }
    };

    useEffect(() => {
        fetchCalculateBookingDetails();
        postCreatePaymentPlan();
    }, []);

    useEffect(() => {
        if (termsDetails) {
            postCreatePaymentPlan();
        }
    }, [termsDetails]);

    useEffect(() => {
        createBasket();
    }, [paymentPlanData, bookingDetailsData]);

    useEffect(() => {
        proceedToBookingDetails();
    }, [createBasketData]);

    return (
        <Box css={styles.wrapper}>
            <Spinner size="big" />
        </Box>
    );
};
